import {makeAutoObservable} from "mobx";
import {getStore, useStore} from "../index";
import {setHtmlRemovePrint} from "@/utils";
import {setFindItemId} from "@/utils/menu";
import axios from "axios";

/**
 * 템플릿 탭 데이터
 * @typedef {object} TemplateTab
 * @property {string} title - 탭 제목
 * @property {string} content - 탭 HTML 내용
 */


/**
 * 템플릿 저장 데이터
 * @typedef {object} TemplateMergeData
 * @property {string} data - HTML 문자열 데이터
 * @property {string} content - HTML 제거 후 텍스트 데이터
 */


/**
 * 현재 선택된 템플릿 정보
 * @typedef {object} CurrentTemplateContent
 * @property {string} menuId - 메뉴 ID
 * @property {string} type - 컨텐츠 타입
 * @property {string} [title] - 메뉴명
 */


/**
 * admin Template 세팅 관리 Store
 */

export class TemplateStore {
    /**
     * 템플릿 탭 리스트
     * @type {Array<TemplateTab>}
     */
    templateTabs = [];

    /**
     * 현재 활성화된 탭 인덱스
     * @type {number|null}
     */
    templateTabActiveIdx = null;

    /**
     * 현재 선택된 템플릿 정보
     * @type {CurrentTemplateContent|null}
     */
    currentTemplateContent = null;

    /**
     * 현재 템플릿 컨텐츠 ID
     * @type {string|null}
     */
    templateContentId = null;
    constructor(store) {
        this.store = store;
        makeAutoObservable(this, {}, {autoBind: true});
    }

    /**
     * 활성 탭 변경
     * @param {number} index - 탭 인덱스
     */
    setTemplateTabActive(index) {
        if (index < 0 || index >= this.templateTabs.length) return;
        this.templateTabActiveIdx = index;
    }

    /**
     * 템플릿 탭 추가
     * @param {string} title - 탭 제목
     */
    setTemplateAddTab(title) {
        this.templateTabs.push({ title:"", content: "" });
        this.templateTabs[this.templateTabs.length-1].title = title;
        this.templateTabActiveIdx = this.templateTabs.length - 1;
    }

    /**
     * 탭 컨텐츠 변경
     * @param {number} index - 탭 인덱스
     * @param {string} value - HTML 내용
     */
    setTemplateContent(index, value) {
        const tab = this.templateTabs[index];
        if (tab) tab.content = value;
    }

    /**
     * 탭 순서 변경
     * @param {number} startIndex - 기존 인덱스
     * @param {number} endIndex - 이동 인덱스
     */
    setReorderTemplateTabs(startIndex, endIndex) {
        const [removed] = this.templateTabs.splice(startIndex, 1);
        this.templateTabs.splice(endIndex, 0, removed);
        if (this.templateTabActiveIdx === startIndex) {
            this.templateTabActiveIdx = endIndex;
        } else if (
            this.templateTabActiveIdx > startIndex && this.templateTabActiveIdx <= endIndex
        ) {
            this.templateTabActiveIdx -= 1;
        } else if (
            this.templateTabActiveIdx < startIndex && this.templateTabActiveIdx >= endIndex
        ) {
            this.templateTabActiveIdx += 1;
        }
    }

    /**
     * 탭 삭제
     * @param {number} index - 삭제 인덱스
     */
    removeTab(index) {
        if (index < 0 || index >= this.templateTabs.length) return;
        this.templateTabs.splice(index, 1);
        if (this.templateTabActiveIdx >= this.templateTabs.length) {
            this.templateTabActiveIdx = this.templateTabs.length - 1;
        }
    }

    /**
     * 탭 데이터를 HTML 문자열로 병합
     * @returns {TemplateMergeData}
     */
    setMergeToHtml() {
        if(this.templateTabs.length > 0){
            let makeTabTitle = "", makeInnerContent = "";
            for(let i = 0; i < this.templateTabs.length; i++) {
                makeTabTitle += `<div class="tabs-item ${i === 0 ? 'active': ''}" data-template-module-tab="tab-${i+1}">${this.templateTabs[i].title}</div>`
                makeInnerContent += `<div class="content-item ${i === 0 ? 'active': ''}" data-template-module-content="tab-${i+1}">${this.templateTabs[i].content}</div>`
            }
            const data = `
                <div class="control-tab-wrap">${makeTabTitle}</div>
                <div class="module-section">${makeInnerContent}</div>
            `
            const rawText = setHtmlRemovePrint(makeInnerContent);
            const content = rawText.replace(/\r?\n/g, " ").replace(/\s+/g, " ").trim();
            return {data, content}
        } else{
            return {
                data: "",
                content: "",
            };
        }
    }

    /**
     * 템플릿 저장
     * @param {string} rootId - 루트 메뉴 ID
     * @returns {Promise<void>}
     */
    async setSaveTemplate(rootId) {
        const { data, content } = this.setMergeToHtml();
        const stringData = data ? JSON.stringify(data) : "";
        const payLoad = {
            data: stringData,
            content,
            menuId: this.currentTemplateContent.menuId,
            type: this.currentTemplateContent.type,
            rootMenuId: rootId
        }
        if(this.templateContentId){
            payLoad["contentId"] = this.templateContentId;
        }
        await axios.post("/api/content", payLoad)
    }

    /**
     * 템플릿 데이터 조회
     * @param {string} id - 메뉴 ID
     * @returns {Promise<void>}
     */
    async setDataTemplate(id) {
        const res = await axios.get(`/api/content?menuId=${id}`);
        if(res.data.data && res.data.data.contents && res.data.data.contents.length > 0){
            const dto = res.data.data.contents[0];
            this.templateContentId = dto._id
            await this.setDetailTemplate(dto._id);
        }
    }

    /**
     * 템플릿 상세 조회
     * @param {string} id - 컨텐츠 ID
     * @returns {Promise<void>}
     */
    async setDetailTemplate(id) {
        const response = await axios.get(`/api/content/detail?contentId=${id}`);
        if(response.data.data){
            const htmlData = response.data.data.content.data;
            if (typeof htmlData === "string" && htmlData.trim().length) {
                const tabs = this.parseTabsFromHtml(htmlData);
                this.templateTabs = tabs;
                this.templateTabActiveIdx = tabs.length ? 0 : null;
            } else {
                this.templateTabs = [];
                this.templateTabActiveIdx = null;
            }
        }
    }

    /**
     * 현재 템플릿 데이터 설정
     * @param {Array<Object>} list - 메뉴 리스트
     * @param {string} id - 메뉴 ID
     * @returns {Promise<void>}
     */
    async setDataCurrentTemplate(list, id) {
        this.initTemplate();
        this.currentTemplateContent = setFindItemId(list, id);
        await this.setDataTemplate(id);
    }

    /**
     * HTML 문자열을 Template Tabs 형태로 변환
     * @param {string} htmlString - HTML 문자열
     * @returns {Array<TemplateTab>}
     */
    parseTabsFromHtml(htmlString) {
        if (!htmlString || typeof htmlString !== "string") return [];
        const wrap = document.createElement("div");
        wrap.innerHTML = htmlString;
        const contentMap = {};
        const contentEls = wrap.querySelectorAll("[data-template-module-content]");
        for (let i = 0; i < contentEls.length; i++) {
            const el = contentEls[i];
            const key = el.getAttribute("data-template-module-content");
            contentMap[key] = el.innerHTML || "";
        }
        const result = [];
        const tabEls = wrap.querySelectorAll("[data-template-module-tab]");
        for (let i = 0; i < tabEls.length; i++) {
            const tabEl = tabEls[i];
            const key = tabEl.getAttribute("data-template-module-tab");
            const title = (tabEl.textContent || "").trim();
            const content = contentMap[key] || "";
            result.push({ title, content });
        }
        return result;
    }


    /**
     * 템플릿 상태 초기화
     */
    initTemplate() {
        this.templateTabs = [];
        this.templateTabActiveIdx = null;
        this.currentTemplateContent = null;
        this.templateContentId = null;
    }
}

/**
 * TemplateStore Hook
 * @returns {TemplateStore}
 */
export const useTemplateStore = () => {
    return useStore().templateStore;
}

/**
 * TemplateStore Getter
 * @returns {TemplateStore}
 */
export const getTemplateStore = () => {
    return getStore().templateStore;
}
